import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DependencyList } from 'react';
import { errorText } from './toast.js';

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /**
   * Refetch, keeping the current data on screen until the answer lands.
   * Stable for the component's lifetime and always runs the latest loader, so
   * a closure that outlives a filter change (an undo window's onDone) reloads
   * the view on screen, not the one that was there when it was created.
   */
  reload: () => Promise<void>;
}

/**
 * Fetch-on-mount with reload. Responses that arrive after the deps changed or
 * the component unmounted are dropped, so a slow first request can never
 * overwrite a newer one.
 */
export function useLoad<T>(load: () => Promise<T>, deps: DependencyList): Loaded<T> {
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });
  const seq = useRef(0);
  const loader = useRef(load);
  // Layout effects run before the deps effect below, so the fetch a deps
  // change triggers already uses this render's loader.
  useLayoutEffect(() => {
    loader.current = load;
  });

  const reload = useCallback((): Promise<void> => {
    const n = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    return loader.current().then(
      (data) => {
        if (n === seq.current) setState({ data, error: null, loading: false });
      },
      (err: unknown) => {
        if (n === seq.current) setState((s) => ({ data: s.data, error: errorText(err), loading: false }));
      },
    );
  }, []);

  useEffect(() => {
    void reload();
    return () => {
      seq.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, reload };
}
